import { ReactNode, useState, useEffect } from 'react';

import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { useToast } from '@/hooks/useToast.hook';

interface ErrorBoundaryProps {
  children: ReactNode;
}

// Component for displaying error
interface ErrorFallbackProps {
  error: Error | null;
  resetErrorBoundary: () => void;
}

const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  return (
    <div className='error-fallback p-4 bg-red-50 border border-red-200 rounded'>
      <h2 className='text-red-800 font-semibold mb-2'>Something went wrong</h2>
      <p className='text-red-600 mb-4'>
        {error?.message || 'An unexpected error occurred'}
      </p>
      <button
        className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
        onClick={resetErrorBoundary}
      >
        Try again
      </button>
    </div>
  );
};

// Functional component for global errors
const GlobalErrorHandler = ({ children }: ErrorBoundaryProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();

  useEffect(() => {
    // Function to check if error originates from our domain
    const isOurError = (error: any, filename?: string): boolean => {
      // Check if file belongs to our application
      if (filename) {
        const currentOrigin = window.location.origin;
        return (
          filename.startsWith(currentOrigin) ||
          filename.startsWith('/') ||
          !filename.includes('://')
        );
      }

      // Check stack trace
      if (error?.stack) {
        const currentOrigin = window.location.origin;
        const stackLines = error.stack.split('\n');
        return stackLines.some(
          (line: string) =>
            line.includes(currentOrigin) ||
            line.includes(window.location.pathname),
        );
      }

      return true; // Consider it ours by default
    };

    const handleError = (event: ErrorEvent) => {
      // Ignore cross-origin errors
      if (event.filename && !isOurError(event.error, event.filename)) {
        console.log('Ignoring cross-origin error:', event.message);
        return;
      }

      // Ignore errors without message (often from other domains)
      if (!event.message || event.message === 'Script error.') {
        return;
      }

      setHasError(true);
      setError(new Error(event.message || 'Unknown error occurred'));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Check if this is our error
      if (!isOurError(event.reason)) {
        console.log('Ignoring cross-origin promise rejection');
        return;
      }

      setHasError(true);
      setError(
        new Error(event.reason?.message || 'Promise rejection occurred'),
      );
    };

    // Add handlers only if we're not in iframe or it's our iframe
    const isInIframe = window !== window.top;
    const isSameOrigin =
      !isInIframe ||
      (() => {
        try {
          return window.top?.location.origin === window.location.origin;
        } catch {
          return false; // Cross-origin iframe
        }
      })();

    if (!isInIframe || isSameOrigin) {
      window.addEventListener('error', handleError);
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
    }

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, []);

  // Reset error state when children change
  useEffect(() => {
    if (hasError) {
      setHasError(false);
      setError(null);
    }
  }, [children]);

  // Show toast only for our errors
  useEffect(() => {
    if (hasError && error) {
      toast(error?.message || 'An unexpected error occurred', 'error', {
        duration: 5000,
        isClosable: true,
        onCloseComplete: () => {
          setHasError(false);
          setError(null);
        },
      });
    }
  }, [hasError, error, toast]);

  return <>{children}</>;
};

// Main ErrorBoundary component using react-error-boundary library
export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const handleError = (error: Error, errorInfo: any) => {
    // Log only errors from our application
    console.error('React Error Boundary caught an error:', error, errorInfo);
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={() => {
        // Optional: Add any cleanup logic here
        window.location.reload();
      }}
    >
      <GlobalErrorHandler>{children}</GlobalErrorHandler>
    </ReactErrorBoundary>
  );
};
